import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronsUpDown, Lock } from 'lucide-react';

import { useConfirm } from '@/components/ui/confirm-dialog';
import { isModeAllowed, useMode, type AllowedEnvironments } from '@/lib/mode/ModeProvider';
import { cn } from '@/lib/utils';
import type { OperationMode } from '@/lib/mode/setCurrentMode';

interface ModeSwitcherProps {
  /**
   * Surface label rendered above the active-mode chip. Set to "Admin
   * console" on the admin layout and "Partner portal" on the portal layout
   * so the surface stays identifiable while the mode is the dominant
   * element.
   */
  subtitle: string;
  className?: string;
}

interface ModeMeta {
  label: string;
  /** Short, plain-English description rendered under the label in the dropdown. */
  description: string;
  /** Tailwind class set covering the dot, gradient, and accent for this env. */
  ring: string;
  dotBg: string;
  glow: string;
}

const MODES: Record<OperationMode, ModeMeta> = {
  production: {
    label: 'Production',
    description: 'Live Adyen — real payments, real money.',
    ring: 'ring-emerald-500/30',
    dotBg: 'bg-emerald-500',
    glow: 'bg-emerald-500/10',
  },
  sandbox: {
    label: 'Sandbox',
    description: 'Adyen Test — isolated, never moves money.',
    ring: 'ring-amber-500/30',
    dotBg: 'bg-amber-500',
    glow: 'bg-amber-500/15',
  },
};

/**
 * Sidebar brand-block dropdown. Stripe/Vercel-influenced: the trigger is a
 * tactile card with a colored status indicator + a subtle ambient glow in
 * the active env's accent color, and the dropdown items are full-width
 * rows with label + one-line description. Disabled items get a lock icon
 * + tooltip ("Contact your admin").
 */
export function ModeSwitcher({ subtitle, className }: ModeSwitcherProps) {
  const { mode, setMode, allowed, isSwitching } = useMode();
  const confirm = useConfirm();
  const active = MODES[mode];

  const onSelect = async (next: OperationMode) => {
    if (next === mode) return;
    const ok = await confirm({
      title: `Switch to ${MODES[next].label}?`,
      description:
        next === 'sandbox'
          ? 'Sandbox hits Adyen Test. Data created here is fully isolated from production — switching signs you out of production.'
          : 'You will leave sandbox and resume working against live data. Switching signs you out of sandbox.',
      confirmLabel: `Switch to ${MODES[next].label}`,
    });
    if (!ok) return;
    await setMode(next);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={isSwitching}
          data-testid="mode-switcher-trigger"
          className={cn(
            'group relative flex w-full items-center gap-3 overflow-hidden rounded-xl',
            'border border-border/60 bg-card/80 px-3 py-2.5 text-left',
            'shadow-sm ring-1 transition-all duration-150',
            'hover:border-border hover:bg-card hover:shadow-md',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-progress disabled:opacity-60',
            active.ring,
            className,
          )}
        >
          {/* Ambient accent glow — tinted in the active env's color */}
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-0 opacity-60 transition-opacity',
              'group-hover:opacity-100',
              `bg-[radial-gradient(circle_at_top_left,_theme(colors.transparent)_30%,_currentColor_140%)]`,
              active.glow,
            )}
          />

          {/* Pulsing status dot */}
          <span aria-hidden className="relative flex h-2.5 w-2.5 shrink-0">
            <span
              className={cn(
                'absolute inset-0 animate-ping rounded-full opacity-60',
                active.dotBg,
              )}
            />
            <span
              className={cn(
                'relative inline-flex h-2.5 w-2.5 rounded-full ring-2 ring-background',
                active.dotBg,
              )}
            />
          </span>

          <span className="relative flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {subtitle}
            </span>
            <span className="truncate text-sm font-semibold text-foreground">
              {active.label}
            </span>
          </span>

          <ChevronsUpDown
            aria-hidden
            className="relative h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform group-hover:text-foreground group-data-[state=open]:rotate-180"
          />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className={cn(
            'z-50 w-[280px] origin-top-left overflow-hidden rounded-xl',
            'border border-border bg-popover text-popover-foreground shadow-2xl',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
        >
          <div className="px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Switch environment
          </div>

          <div className="px-1.5 pb-1.5">
            <ModeMenuItem
              mode="production"
              active={mode === 'production'}
              allowed={allowed}
              onSelect={onSelect}
            />
            <ModeMenuItem
              mode="sandbox"
              active={mode === 'sandbox'}
              allowed={allowed}
              onSelect={onSelect}
            />
          </div>

          <div className="border-t border-border/60 bg-muted/40 px-3 py-2 text-[10px] leading-snug text-muted-foreground">
            Switching environments signs you out of the other one.
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

interface ModeMenuItemProps {
  mode: OperationMode;
  active: boolean;
  allowed: AllowedEnvironments;
  onSelect: (mode: OperationMode) => void;
}

function ModeMenuItem({ mode, active, allowed, onSelect }: ModeMenuItemProps) {
  const enabled = isModeAllowed(allowed, mode);
  const meta = MODES[mode];

  return (
    <DropdownMenu.Item
      disabled={!enabled}
      onSelect={(e) => {
        // Don't auto-close while the confirm dialog renders; radix closes
        // by default but we want to keep the dropdown contextually open.
        e.preventDefault();
        if (enabled) onSelect(mode);
      }}
      data-testid={`mode-switcher-item-${mode}`}
      title={enabled ? undefined : 'Contact your admin to request access'}
      className={cn(
        'group/item flex w-full cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2 outline-none transition-colors',
        'focus:bg-accent focus:text-accent-foreground',
        'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
        active && 'bg-accent/60',
      )}
    >
      {/* Status dot */}
      <span aria-hidden className="mt-1.5 flex h-2 w-2 shrink-0">
        <span className={cn('inline-flex h-2 w-2 rounded-full ring-2 ring-background', meta.dotBg)} />
      </span>

      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-foreground">{meta.label}</span>
          {!enabled && (
            <Lock aria-hidden className="h-3 w-3 text-muted-foreground/70" />
          )}
        </span>
        <span className="mt-0.5 truncate text-[11px] text-muted-foreground">
          {meta.description}
        </span>
      </span>

      {active && (
        <Check
          aria-hidden
          className="mt-1 h-4 w-4 shrink-0 text-foreground transition-transform group-data-[state=checked]/item:scale-110"
        />
      )}
    </DropdownMenu.Item>
  );
}

/**
 * Colored status dot. Green for production, amber for sandbox.
 * Standalone so audit-row chips + the trigger + menu items share one
 * source of truth for the colour mapping.
 */
export function ModeDot({ mode, className }: { mode: OperationMode; className?: string }) {
  const meta = MODES[mode];
  return (
    <span
      aria-hidden
      data-testid={`mode-dot-${mode}`}
      className={cn(
        'inline-block h-2 w-2 shrink-0 rounded-full ring-2 ring-background',
        meta.dotBg,
        className,
      )}
    />
  );
}
